<template>
	<v-app>
		<NavigationDrawer v-if="isLoggedIn && !loading" />
		<ModalManager v-if="!loading" />
		<v-main class="content bg-background">
			<Loading v-if="loading" style="height: 100%"> </Loading>
			<LegacySyncUpdateBanner v-if="isLoggedIn && !loading" />
			<ContentFrame v-if="!loading" :dense="false">
				<!--
					`hideBreadcrumb` in den Route-Metadaten nimmt die Brotkrume samt
					ihrem Container heraus (Belegprüfungs-Cockpit): der Slot wird gar
					nicht erst uebergeben, sonst bliebe der leere Container stehen.
				-->
				<template
					v-if="
						isLoggedIn &&
						$vuetify.display.mdAndUp &&
						!$route.meta.hideBreadcrumb
					"
					#header
				>
					<Breadcrump />
				</template>
			</ContentFrame>
			<CookieConsent v-if="!loading"></CookieConsent>
			<v-snackbar
				v-if="isLoggedIn && !loading"
				v-model="isRefreshing"
				:timeout="-1"
				color="primary"
				multi-line
			>
				Ihre Anmeldung wird aktualisiert
				<template #actions>
					<v-progress-circular
						color="warning"
						indeterminate
					></v-progress-circular>
				</template>
			</v-snackbar>
			<UpdateDialog v-if="!loading" />
			<VueQueryDevtools v-if="!loading && !isCypress" />
			<Toast v-if="!loading" />
			<Errors v-if="isLoggedIn"></Errors>
		</v-main>
		<Footer />

		<TourFooter v-if="isLoggedIn" />
	</v-app>
</template>

<script setup lang="ts">
import ContentFrame from '@/components/elements/ContentFrame.vue'
import ModalManager from '@/components/elements/ModalManager.vue'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import Breadcrump from 'components/elements/Breadcrump.vue'
import NavigationDrawer from 'components/elements/Drawer.vue'
import Toast from 'components/elements/Toast.vue'
import UpdateDialog from 'components/modules/UpdateDialog.vue'
import LegacySyncUpdateBanner from 'components/elements/LegacySyncUpdateBanner.vue'
import Footer from 'elements/Footer.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { isCypress as isCypressEnv } from '@/helpers/isCypress'
import { storeToRefs } from 'pinia'
import { useAppSetup } from './composables/appSetup'
import { useLoadOfflineComponents } from './composables/loadOfflineComponents'
import { useConnectionStatus } from './composables/useConnectionStatus'
import { useAppTheme } from './composables/appTheme'
import { useTour } from './composables/useTour'
import { useAuthStore, useToastStore, useUserStore } from './stores/system'
import { takeLogoutReason } from './stores/system/user'
import { find } from 'lodash'
import { useI18n } from 'vue-i18n'
import CookieConsent from './components/elements/CookieConsent.vue'
import Loading from './components/templates/Loading.vue'
import Errors from './components/elements/Errors/Errors.vue'
import TourFooter from './components/elements/TourFooter.vue'
import { onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const { isRefreshing } = storeToRefs(useAuthStore())
const { isLoggedIn, loading } = storeToRefs(useUserStore())
const isPROD = import.meta.env.PROD
// Der Floating-Button der Vue-Query-Devtools liegt unten rechts genau auf den
// Senden-/Hinzufügen-Knöpfen und fängt deren Klicks ab — unter Cypress bleibt
// er deshalb aus. isCypress() deckt dabei alle drei Signale ab (window.Cypress,
// sessionStorage-Flag über Reloads hinweg, Coverage-Build).
const isCypress = isCypressEnv()

// AAR-1482 test hook: expose the app's ACTUAL installed query client so
// Cypress can inspect the cache (page-query observer counts). useQueryClient()
// returns the client VueQueryPlugin installed, independent of how the plugin
// is wired — do not expose the named export from plugins/queryClient here.
if (isCypressEnv()) {
	;(window as any).__queryClient = useQueryClient()
}

useAppSetup()
useAppTheme()

// AAR-300/AAR-1677: Eine erzwungene Abmeldung (403 auf /whoami, etwa bei einem
// deaktivierten Teammitglied) beendet die Sitzung per Vollreload — der Toast, den der
// Aufrufer danach setzt, geht dabei verloren. Ausgerechnet der erklärt, warum der
// Zugang nicht mehr funktioniert. logout() hinterlegt den Grund, hier wird er nach dem
// Neuaufbau nachgereicht.
//
// Bewusst hier und nicht in Errors.vue: das hängt an `v-if="isLoggedIn"` und existiert
// auf der Anmeldeseite gar nicht — genau dort landet der Reload aber.
//
// Nachgeschlagen wird in der i18n-Tabelle wie in Error.vue, nicht in
// constants/errorMessages.ts: letztere ist bewusst rein deutsch (ShowError.vue rendert
// sie ohne Uebersetzung), und die Abmeldung landet auf der Anmeldeseite, wo die
// Sprache des Nutzers gelten muss. `invalid_subscription` gibt es mehrfach mit
// unterschiedlichem `code` — ohne den Code traefe der Lookup die falsche Zeile.
const { tm } = useI18n()

onMounted(() => {
	const reason = takeLogoutReason()
	if (!reason) return
	const messages = tm('component.error.errorMessages')
	const known =
		reason.error === 'invalid_subscription'
			? find(messages, { error: reason.error, code: reason.data?.code })
			: find(messages, { error: reason.error })
	if (!known) return
	useToastStore().toastError({
		header: known.header,
		message: known.message,
	})
})

// AAR-1670: connectivity signal for ShowError.vue, in ALL environments. Kept
// separate from useLoadOfflineComponents because that one also flips
// userStore.isOnline, which gates the not-yet-enabled query persister.
useConnectionStatus()

if (!isPROD) {
	useLoadOfflineComponents()
}

// Auto-start the end-user onboarding tour after first login completes.
const tour = useTour()
const route = useRoute()
let autoStartTimer: ReturnType<typeof setTimeout> | null = null
watch(
	[isLoggedIn, loading, () => route.name],
	([loggedIn, isLoading, routeName], previous) => {
		// Any pending auto-start is invalidated by ANY state change — the
		// timer's callback re-checks conditions anyway, but clearing here
		// avoids the brief window where the user navigates off the dashboard
		// while we're still queued and then yanked back.
		if (autoStartTimer) {
			clearTimeout(autoStartTimer)
			autoStartTimer = null
		}
		// Tear down any in-flight tour the moment the user logs out, so the
		// module-level currentDriver reference doesn't leak across sessions
		// in the same tab. Logout teardown must NOT mark the user as
		// "skipped" — that would suppress the next auto-start for them.
		const wasLoggedIn = previous?.[0]
		if (wasLoggedIn && !loggedIn) {
			tour.stop({ reason: 'logout' })
			return
		}
		if (!loggedIn || isLoading) return
		if (routeName !== 'dashboard') return
		if (!tour.shouldAutoStart()) return
		// Defer past the current paint so dashboard tiles can mount.
		autoStartTimer = setTimeout(() => {
			autoStartTimer = null
			// Re-check at fire-time — the user may have navigated away or
			// logged out during the wait.
			if (!isLoggedIn.value || loading.value) return
			if (route.name !== 'dashboard') return
			if (!tour.shouldAutoStart()) return
			tour.start()
		}, 400)
	},
	{ immediate: true }
)
</script>
<style lang="scss">
html {
	font-size: 15px;

	/* Design-Token für die Navigations-Schriftgröße (Haupt-Drawer).
	   rem-basiert → skaliert konsistent über Desktop/Tablet/Mobile.
	   Vuetify setzt für .v-list-item--nav nur 0.8125rem/1rem, was bei
	   font-size:15px effektiv ~12px ergibt und von Kunden als zu klein
	   empfunden wurde (AAR-1469). */
	--nav-item-font-size: 0.9375rem;
	--nav-item-line-height: 1.25rem;
}

/* Override scoped auf den Haupt-Navigations-Drawer (.nav).
   Spezifität 0-4-0 schlägt Vuetifys 0-2-0 ohne !important und trifft
   weder Kontextmenüs noch BuildingNav. Titel haben bereits
   ellipsis/nowrap → kein Layout-Overflow durch die größere Schrift. */
.v-navigation-drawer.nav .v-list-item--nav .v-list-item-title {
	font-size: var(--nav-item-font-size);
	line-height: var(--nav-item-line-height);
}

.v-label {
	font-size: 14px !important;
}

/* AAR-964 (erweitert): Icon-Buttons sind auf Touchscreens schwer zu treffen
   (~28–36px je nach size/density). Auf Mobile (xs) bekommen ALLE Icon-only-Buttons
   ein 44x44px-Tap-Target (WCAG 2.5.5 / HIG), ohne die Glyph-Größe zu verändern.
   Fixes width/height (kein min-*): Vuetify pinnt die Button-Größe via inline-grid
   + --v-btn-height, was min-width/min-height nicht überschreiben kann. Nur
   .v-btn--icon, damit Text-Buttons und dichte Desktop-Layouts unberührt bleiben. */
@media (max-width: 599.98px) {
	.v-btn--icon {
		width: 44px !important;
		height: 44px !important;
	}

	/* AAR-964 (Glyph): Vuetify verkleinert Icons in Buttons standardmäßig
	   (--v-icon-size-multiplier: 0.857 in Text-Buttons, 1 in Icon-Buttons).
	   Auf Mobile sollen die Glyphen in ALLEN Buttons größer/besser sichtbar
	   sein. Wir überschreiben den Multiplier einheitlich; .v-btn .v-icon matcht
	   jeden Button-Typ (Icon-only, Text+Icon, success/primary-Action-Buttons,
	   deren Label auf xs ausgeblendet ist). Wert ist die zentrale Stellschraube. */
	.v-btn .v-icon {
		--v-icon-size-multiplier: 1.3 !important;
	}
}

#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	/* AAR-1870: maximale Inhaltsbreite (ContentFrame.vue), zentrale Stellschraube */
	--content-max-width: 1440px;
}

.light-grey--text {
	color: rgba(0, 0, 0, 0.6) !important;
}

.breakword {
	word-break: break-word;
}

.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg {
	background-color: rgba(0, 0, 0, 0.12) !important;
}

.stepperHeader {
	box-shadow: none !important;

	.v-stepper__label {
		text-shadow: none;
		font-size: 16px;
	}

	.v-stepper__step--active:not(.error--text) {
		font-weight: bold;

		.v-stepper__step__step {
			background-color: var(--v-secondary-base) !important;
		}
	}
}

.limitAccess .v-label {
	font-size: 13px;
}

#greetingText p {
	margin-bottom: 0 !important;
	color: var(--v-text-base) !important;
}

.v-label,
p,
:not(td):not(.v-btn__content):not(.v-btn):not(a):not(.v-tooltip__content):not(
		.noteBadgeColor
	):not(.data-table__link):not(.v-alert__content)
	> span:not(.colorDefault):not(.v-stepper__step__step):not(.v-badge):not(
		.v-badge__badge
	):not(.v-chip__content):not(.v-chip),
:not(.v-toolbar__title) > b,
.headline:not(a):not(.colorDefault),
.sitePolicyContainer,
h2:not(.changed):not(.created) > span {
	color: var(--v-text-base);
}

.ql-picker-label,
.ql-picker-item {
	color: #000 !important;
}

.iziToast-body {
	font-family: Roboto, Helvetica, sans-serif;
}

.theme--light.v-list .v-list_item--link:hover,
.theme--light.v-list:not(.v-list--flat)
	.v-list-item:hover:not(.autocompleteTotal),
.theme--light.v-list .v-list-group__header:hover {
	background-color: transparent !important;
	color: var(--v-secondary-base) !important;
}

.disabled * {
	color: rgba(0, 0, 0, 0.38) !important;
}

.v-data-footer__pagination {
	margin: 0 !important;
}

.scrollable-content {
	max-height: calc(75vh);
}

/* Auf Mobile (xs/fullscreen) bestimmt die Flex-Spalte im BaseStepper die Höhe
   des Scrollbereichs; keine fixe vh-Höhe mehr, die hinter der Button-Leiste
   verschwand. */

.text--overflow-hidden {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* AAR-178: Ziel eines Deeplinks aus der Benachrichtigungsmail (Konto-Panel,
   Buchungszeile). Global und genau einmal definiert, weil zwei Komponenten in
   zwei Modulen dieselbe Optik brauchen.
   Teal ist hier erlaubt (DESIGN.md „One Teal Rule": das eine lebende Element
   der Seite) und trägt Bedeutung statt Zierde („Earned Color Rule").
   Haarlinie als inset box-shadow statt border: verschiebt kein Layout, ist kein
   Akzentstreifen (DESIGN.md verbietet border-left > 1px) und kollidiert nie mit
   der Hintergrundfarbe des Prüfzustands. Kein Schatten, keine Animation
   („Flat-By-Default"). Farbe ist nie das einzige Signal — am Ziel steht
   zusätzlich der Chip „Aus Ihrer Benachrichtigung" (WCAG AA / BITV). */
.deeplink-target {
	box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
	/* !important, weil .v-list-item und .v-expansion-panel den Radius selbst auf
	   derselben Spezifität (0-1-0) setzen — ohne das entscheidet die Reihenfolge
	   im Bundle, ob die Haarlinie runde oder eckige Ecken bekommt. */
	border-radius: 4px !important;
}

/* Tönung nur, wenn die Zeile keine fachliche Zustandsfarbe trägt: sie ersetzt
   die Zebra-Streifung, darf aber die Prüfzustandsfarbe der Buchung nie
   verdecken. Dort bleiben Haarlinie und Chip als Signal.
   !important wie bei Vuetifys eigenen bg-*-Utilities (die Zebra-Klasse
   bg-surface-darken-1 gewinnt genauso): .v-list-item--variant-text setzt
   `background: transparent` auf derselben Spezifität. */
.deeplink-target--tinted {
	background-color: rgba(var(--v-theme-primary), 0.06) !important;
}

/* driver.js-Tour: Kopfzeile in der Mandanten-Primärfarbe (mit Johannes
   abgestimmt, 2026-10-16), Fläche aus den surface-Tokens, damit das Fenster
   immer zur Mandanten-CI passt. */
.driver-popover.aaremo-tour-popover {
	background-color: rgb(var(--v-theme-surface));
	color: rgb(var(--v-theme-on-surface));
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	font-family: Roboto, Helvetica, sans-serif;
	// Feste Breite statt nur max-width: am rechten Rand schrumpfte das
	// Fenster nach dem Positionieren, der Text brach um, und das nach unten
	// verankerte Fenster wuchs nach oben über sein eigenes Element. Auf
	// schmalen Bildschirmen höchstens Bildschirmbreite minus Rand.
	width: min(380px, calc(100vw - 16px));
	max-width: min(380px, calc(100vw - 16px));
	padding: 0;
	// Geringe Höhe (Handy quer, App mit Tastatur): Titel und Footer bleiben
	// stehen, nur der Text scrollt.
	max-height: calc(100dvh - 16px);
	display: flex;
	flex-direction: column;

	// Bis der Highlight-Kasten angekommen ist (useTour, onHighlighted). Ohne
	// `animation: none` überschriebe das Einblenden von driver.js die Opazität;
	// beim Entfernen der Klasse startet es neu und blendet das Fenster ein.
	&.aaremo-tour-popover--pending {
		opacity: 0;
		animation: none;
	}

	.driver-popover-title {
		background-color: rgb(var(--v-theme-primary));
		color: rgb(var(--v-theme-on-primary));
		font-weight: 600;
		font-size: 15px;
		padding: 12px 16px;
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
		margin: 0;
	}

	.driver-popover-close-btn {
		color: rgb(var(--v-theme-on-primary));
		opacity: 0.85;
		top: 8px;
		right: 8px;
		font-size: 18px;
		&:hover {
			opacity: 1;
			color: rgb(var(--v-theme-on-primary));
		}
	}

	.driver-popover-title {
		flex: 0 0 auto;
	}

	.driver-popover-description {
		padding: 16px;
		font-size: 14px;
		line-height: 1.5;
		flex: 1 1 auto;
		min-height: 0;
		overflow-y: auto;
		overscroll-behavior: contain;

		// Tastenkürzel in der Einführung der Belegprüfung.
		kbd {
			display: inline-block;
			min-width: 1.6em;
			padding: 0 4px;
			border: 1px solid rgba(var(--v-theme-on-surface), 0.3);
			border-radius: 4px;
			font-family: inherit;
			font-size: 12px;
			line-height: 1.5;
			text-align: center;
		}
	}

	.aaremo-tour-keys {
		width: 100%;
		margin-bottom: 8px;
		border-collapse: collapse;

		td {
			padding: 3px 8px 3px 0;
			white-space: nowrap;
		}
	}

	.driver-popover-footer {
		// Driver's built-in footer is unused — we inject our own
		// .aaremo-tour-footer-slot above it via onPopoverRender.
		display: none;
	}

	// driver.js builds the arrow with border-trick: 3 borders are
	// transparent and 1 is the popover bg color. We must only override the
	// VISIBLE side per direction; using `border-color` shorthand turns the
	// triangle into a filled square ("white pixel").
	// side-bottom → arrow sits at top of popover, points up → bottom border visible
	// side-top    → arrow sits at bottom of popover, points down → top border visible
	// side-left   → arrow sits at right of popover, points right → left border visible
	// side-right  → arrow sits at left of popover, points left → right border visible
	.driver-popover-arrow-side-bottom.driver-popover-arrow {
		border-bottom-color: rgb(var(--v-theme-surface));
	}
	.driver-popover-arrow-side-top.driver-popover-arrow {
		border-top-color: rgb(var(--v-theme-surface));
	}
	.driver-popover-arrow-side-left.driver-popover-arrow {
		border-left-color: rgb(var(--v-theme-surface));
	}
	.driver-popover-arrow-side-right.driver-popover-arrow {
		border-right-color: rgb(var(--v-theme-surface));
	}

	.aaremo-tour-footer-slot {
		flex: 0 0 auto;
		padding: 0 16px 14px;
		// Defensive: driver.js' .driver-active *{pointer-events:none} can
		// out-win the .driver-popover *{pointer-events:auto} override in
		// some cascade orderings (browser-dependent). Force auto on every
		// descendant so the tour buttons remain clickable.
		pointer-events: auto !important;
		* {
			pointer-events: auto !important;
		}
	}

	// driver.js applies `.driver-popover * { font-family: ... }` which beats
	// FontAwesome's class rule on equal specificity (later cascade order).
	// Force FA Pro back onto every <i> / .v-icon inside our footer slot.
	.aaremo-tour-footer-slot i,
	.aaremo-tour-footer-slot .v-icon {
		font-family: 'Font Awesome 7 Pro' !important;
	}
}

.aaremo-tour-footer {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 8px;

	// Touch: Knöpfe mindestens 44 px hoch.
	@media (pointer: coarse) {
		.v-btn {
			min-height: 44px;
		}
	}
}

/* Karten im Seiteninhalt tragen keinen Dropshadow. Das galt bisher nur fuer
   die aeussere Seiten-Card; verschachtelte Karten (Kacheln, Info-Bloecke,
   Notizkarten) brachten weiter Vuetifys Default-Schatten mit.
   Overlays sind bewusst ausgenommen: Dialoge und Menues haengen im
   .v-overlay-container am <body>, also ausserhalb von .v-main, und duerfen
   weiter schweben — ein Schatten als Reaktion auf einen Zustand, nicht als
   Dauerzustand. */
.v-main .v-card {
	box-shadow: none;
}

/* ---------------------------------------------------------------------------
   Listenseiten verschmelzen mit dem Canvas.

   Der Kopfbereich einer Seite (Titel, Suche, Filter, Umschalter) laeuft auf dem
   grauen Hintergrund mit; weiss bleibt nur die Tabelle als Arbeitsflaeche.

   Getroffen wird ueber die Struktur statt ueber eine Klasse pro Seite: die
   Card einer Listenseite ist DIREKTES Kind des Seiten-Containers, und der
   wiederum direktes Kind von .content-frame (ContentFrame.vue). Alles
   Verschachtelte — Cards in Dialogen, in Spalten (v-col), in Karten-Rastern
   oder auf Detailseiten — sitzt nie an dieser Stelle und bleibt unberuehrt.
   Damit gilt die Optik automatisch auf jeder Listenseite, auch in der
   Verwaltung, ohne dass eine einzige Seitendatei markiert werden muss.

   Der Pfad ueber .content-frame ist noetig, nicht nur Kosmetik: <v-container>
   kommt auch verschachtelt vor (etwa in <v-card-text> der HWA-Detailseite).
   Ein blosses `.v-main .v-container > .v-card` macht dadurch Detailseiten
   flach, die es nicht werden sollen — im Browser nachgewiesen.

   Einzelne Seite ausnehmen: `flush-exempt` an die Card.
   --------------------------------------------------------------------------- */
.v-main {
	/* Schmaler Rand links und rechts, damit der Inhalt nie an die Navigation
	   oder den Fensterrand stoesst. Auf dem Handy ist das nicht nur Optik: eine
	   Tabelle, die bis an den Rand laeuft, faengt dort die Wischgeste ab (sie
	   scrollt selbst horizontal) — der Streifen laesst die Seite weiter
	   vertikal scrollen. Einmal hier aendern wirkt ueberall. */
	--flush-gutter: 12px;
}

/* ---------------------------------------------------------------------------
   Mandantenfarben als Flaechen.

   Seit dem flachen Layout liegen die Arbeitsflaechen direkt auf dem
   Mandanten-Hintergrund. Reines Weiss steht dort als Fremdkoerper — bei Boehm
   ein weisser Kasten mitten im Gelb.

   Das Problem an der Mandantenfarbe ist ihre Spannweite: `bgcolor_f` hat keine
   Schranke. Boehm faehrt `#f0d522`, ein Knallgelb (OKLCH-Chroma ~0.17);
   S-Struktur setzt gar keinen Hintergrund und bleibt bei `#f5f5f5`; ein
   dritter Mandant steht auf `#911B1B`, als Flaeche fast schwarz. Wer die Farbe
   roh nimmt, baut fuer den einen ein Neonschild und fuer den anderen nichts.

   Deshalb die Aufteilung: DER MANDANT LIEFERT DEN FARBTON, DAS DESIGNSYSTEM
   DIE INTENSITAET. `--tenant-hue` behaelt Farbton und Helligkeit der
   Mandantenfarbe und kappt nur die Saettigung. Ein lautes Gelb kommt damit
   gedaempft an, ein dezentes Grau bleibt unveraendert grau — beide durchlaufen
   dieselbe Regel.

   `l` bleibt bewusst unangetastet: im dunklen Theme gibt es gar keine
   Mandantenfarben (`themes.dark` ueberschreibt nur nav/text/table), dort steht
   in `--v-theme-background` ein dunkles Grau. Eine fest gesetzte Helligkeit
   wuerde die Flaechen dort aufhellen.
   --------------------------------------------------------------------------- */
.v-application,
.v-overlay-container {
	/* Fallback fuer Engines ohne relative Farbsyntax: ungedaempft, dafuer
	   weiter unten schwaecher eingemischt — die Toenung faellt etwas anders
	   aus, nichts bricht. */
	--tenant-hue: rgb(var(--v-theme-background));

	/* Neutrale Stufenleiter, gegen `on-surface` gemischt statt gegen einen
	   festen Grauwert: damit kippt sie im dunklen Theme von selbst mit. Sie
	   garantiert den Helligkeitsunterschied auch dort, wo der Mandant gar
	   keine Farbe gesetzt hat — sonst waere eine Tabellen-Kopfzeile bei
	   S-Struktur schlicht unsichtbar.

	   16 % und nicht 8 %: mit 8 % lag der Schritt von der Flaeche zur Kopfzeile
	   bei 1,10:1 — nachgemessen, und zwar bei JEDEM Mandanten gleich, weil die
	   Stufe aus der Mischung kommt und nicht aus der Mandantenfarbe. Auf einem
	   Handydisplay bei Tageslicht ist das nicht vorhanden. Der Textkontrast hat
	   reichlich Luft (Fliesstext liegt bei 12,9–15,3:1), die Stufe darf also
	   deutlich kraeftiger ausfallen. */
	--step-1: rgb(var(--v-theme-surface));
	--step-2: color-mix(
		in oklab,
		rgb(var(--v-theme-on-surface)) 16%,
		rgb(var(--v-theme-surface))
	);

	/* OFFEN: Setzt ein Mandant `bgcolor_f` auf Weiss, faellt die Arbeitsflaeche
	   exakt mit dem Canvas zusammen (1,00:1) — Tabelle, Karten und Suchfeld
	   werden ein einziges weisses Blatt, und nur die Kopfzeile gliedert noch.
	   Kein konstruierter Fall: client 326 hat genau `#FFFFFF` stehen.

	   Eine Haarlinie an der Tabellenkante haette das aufgefangen, widerspricht
	   aber der Flat-Design-Vorgabe (CLAUDE.md) und wurde daher wieder entfernt.
	   Der saubere Weg fuehrt ueber die Helligkeit statt ueber einen Rahmen: die
	   Flaeche muesste sich garantiert vom Canvas absetzen, statt sich nur
	   einzufaerben. Das aendert die Toenung fuer ALLE Mandanten und ist damit
	   eine eigene Entscheidung, keine Nebenwirkung dieser hier. */

	/* Zwei Stufen, mehr braucht es nicht: `-1` ist die Arbeitsflaeche (Tabelle,
	   Karte, Suchfeld, Dialog), `-2` traegt Struktur statt Inhalt
	   (Tabellenkopf, Dialogkopf, Knopf, leerer Zustand). Beide mit DERSELBEN
	   Toenung, damit die Leiter als eine Flaeche gelesen wird und nicht als
	   zwei verschiedene Farben.

	   Eine dritte Stufe fuer Zebra-Streifen gab es hier kurz — sie ist wieder
	   raus, weil Vuetify die Streifen ueber `striped="odd"` bereits selbst
	   zeichnet und dabei halbtransparent bleibt. Siehe unten bei den Tabellen. */
	--tenant-surface-1: color-mix(in oklab, var(--tenant-hue) 18%, var(--step-1));
	--tenant-surface-2: color-mix(in oklab, var(--tenant-hue) 18%, var(--step-2));
}

@supports (color: oklch(from white l c h)) {
	.v-application,
	.v-overlay-container {
		--tenant-hue: oklch(from rgb(var(--v-theme-background)) l min(c, 0.09) h);
	}
}

/* Im dunklen Theme faellt die Stufenleiter flach. Die Vorgabe dort ist reines
   Schwarz/Weiss ohne Grau (CLAUDE.md) — und genau diese Helligkeitsabstufung
   war es, die das flache Layout aus den verschachtelten Karten entfernt hat:
   im Hellen unsichtbar, im Dunklen pro Block ein sichtbar hellerer Kasten.
   Getrennt wird dort weiter ueber die Haarlinie, wie bisher.

   Steht bewusst NACH dem Block oben: beide Klassen sitzen auf demselben
   Element, gleiche Spezifitaet, es gewinnt die spaetere Regel. */
.v-theme--dark {
	--step-2: rgb(var(--v-theme-surface));
}

/* Zwei Bauarten kommen vor: die meisten Seiten setzen einen <v-container>,
   einige (Faqs/Categories/List.vue) direkt eine <v-col>. Die Optik gilt fuer
   beide. */
.content-frame > .v-container > .v-card:not(.flush-exempt),
.content-frame > .v-col > .v-card:not(.flush-exempt) {
	background: transparent;
	border-radius: 0;
	box-shadow: none;
	/* Auch der Rahmen faellt weg: manche Seiten setzen `border` an der Card
	   (z.B. Definitions.vue), was ohne eigene Flaeche nur einen Kasten um den
	   Canvas zieht. */
	border: none;

	/* Die Variante `text` ist eine Card ohne eigene Flaeche — der Breadcrumb
	   ist so eine. Bekaeme deren Textkoerper Weiss, laege ueber der Seite ein
	   weisser Streifen. */
	&:not(.v-card--variant-text) > .v-card-text {
		background-color: var(--tenant-surface-1);
	}

	/* v-list bringt von Haus aus `surface` mit. Auf der grauen Flaeche wurde
	   daraus je ein weisser Kasten pro Block — auf der Personenseite drei
	   Stueck in unterschiedlichen Hoehen nebeneinander. Die Listen gliedern
	   hier nur, die Flaeche stellt der Textkoerper. */
	.v-list,
	.v-list-item {
		background: transparent;
	}
}

/* Der Rand-Ausgleich betrifft nur die Container-Variante: <v-container> bringt
   16px Padding mit, davon bleibt der Gutter stehen, der Rest wird
   herausgerechnet. Eine <v-col> hat von sich aus 12px und trifft den Gutter
   damit bereits — dort waere jeder Ausgleich falsch. */
.content-frame > .v-container > .v-card:not(.flush-exempt) {
	margin-inline: calc(var(--flush-gutter) - 16px);
}

/* Viele Seiten schalten das Container-Padding bei kleinen Breakpoints selbst
   ab (`pa-0`) — aber nicht alle und nicht am selben Breakpoint: Types.vue
   nutzt xsOnly, Buildings.vue xs, PersonList.vue gar nicht, Roles.vue ein
   `display.cs`, das es in Vuetify nicht gibt und daher nie greift. Deshalb
   wird der tatsaechliche Zustand des Containers abgefragt statt ein Breakpoint
   geraten: ohne Padding gibt es nichts herauszurechnen, der Gutter wird direkt
   gesetzt. */
.content-frame > .v-container.pa-0 > .v-card:not(.flush-exempt) {
	margin-inline: var(--flush-gutter);
}

/* Das Dashboard sitzt wie jede Listenseite in einem <v-container> direkt unter
   .content-frame (pa-0, Gutter setzt .dash__inner) und hat keinen eigenen
   Seitenhintergrund. Seine Bausteine sind v-cards in der Zellen-Struktur
   (.dash-cell) und bekommen die Arbeitsflaeche ueber die Karten-Regel unten. */

/* Karten auf Detailseiten. Dieselbe Frage wie bei der eingebetteten Tabelle:
   eine Detailseite besteht aus reinweissen Kaesten, die auf Boehms Gelb als
   Fremdkoerper stehen. Sie tragen dieselbe Arbeitsflaeche wie die Tabellen,
   damit eine Seite als EINE Flaeche gelesen wird und nicht als Sammlung
   verschieden heller Rechtecke.

   Bewusst OHNE `!important`: Vuetifys Farb-Utilities (`bg-primary`,
   `bg-warning`, die farbigen Notizkarten) setzen ihren Hintergrund mit
   `!important` und behalten ihn damit von selbst. Eine Ausnahmeliste braucht
   es dafuer nicht.

   Die Listenseiten-Card bleibt unberuehrt: ihr Selektor traegt drei Klassen
   und sticht diesen hier, ihre Flaeche ist weiter transparent. Dialoge haengen
   im .v-overlay-container ausserhalb von .v-main und sind ebenfalls nicht
   betroffen. */
.v-main .v-card--variant-flat,
.v-main .v-card--variant-elevated {
	background-color: var(--tenant-surface-1);
}

/* Dialoge haengen im .v-overlay-container am <body> und damit AUSSERHALB von
   .v-main — jede Regel unten laeuft an ihnen vorbei. Ohne diesen Block waere
   ein Dialog das einzige weisse Element geblieben, und eine Tabelle DARIN
   haette anders ausgesehen als dieselbe Tabelle auf der Seite dahinter.

   Deshalb dieselben Stufen noch einmal fuer den Overlay-Zweig. Die Alternative
   waere, jede Regel unten auf beide Orte zu erweitern; das doppelt zwanzig
   Selektoren statt vier. */
.v-overlay-container {
	.v-card--variant-flat,
	.v-card--variant-elevated {
		background-color: var(--tenant-surface-1);
	}

	.v-table {
		background-color: var(--tenant-surface-1);
	}

	.v-table thead th {
		background-color: var(--tenant-surface-2) !important;
	}

	.v-field--variant-solo,
	.v-field--variant-solo-filled {
		background-color: var(--tenant-surface-1);
	}
}

/* ---------------------------------------------------------------------------
   Tabellen und Suchleiste auf der Mandantenflaeche.

   Bewusst fuer JEDE Tabelle unterhalb von .v-main, nicht nur fuer die Card
   einer Listenseite. Die Toenung hing zuerst an der Listen-Card; eine Tabelle
   auf einer Detailseite (etwa die Dokumente eines Handwerkerauftrags) sitzt
   aber tief in Tabs und Textkoerpern verschachtelt, fiel durch den Selektor
   und blieb als weisser Block im Gelb stehen. Eine Regel, jede Tabelle.
   --------------------------------------------------------------------------- */
.v-main .v-table {
	background-color: var(--tenant-surface-1);
}

/* Die Kopfzeile traegt Struktur, keinen Inhalt — sie steht eine Stufe tiefer.
   Ueber die neutrale Leiter, nicht ueber die Mandantenfarbe allein: bei einem
   Mandanten ohne eigenen Hintergrund gaebe es sonst gar keinen Unterschied
   zur Tabelle darunter. Vuetify setzt hier `background: rgb(var(--v-theme-surface))`
   auf die Zelle, deshalb auf `th` und nicht auf `thead`. */
.v-main .v-table thead th {
	background-color: var(--tenant-surface-2) !important;
}

/* KEINE eigene Zebra-Regel hier — das waere doppelt gemoppelt.
   `TablePresentation.vue` setzt `striped="odd"` auf jede Tabelle der App,
   woraus Vuetify einen halbtransparenten Overlay auf den ungeraden Zeilen
   macht. Der ist der bessere Streifen, und zwar aus zwei Gruenden: er erbt die
   Toenung der Flaeche unter sich automatisch (ueber `--tenant-surface-1`
   bekommt er sie also geschenkt), und er laesst Hover und Auswahl
   durchscheinen. Ein opaker zweiter Streifen haette beides zugedeckt und auf
   denselben Zeilen ein konkurrierendes Muster erzeugt. */

/* Listen malen keine eigene Flaeche, sie erben die darunter. `v-list` bringt
   von Haus aus `surface` mit — auf einer Detailseite war das ein weisser Block
   in der getoenten Karte. Statt Listen eine eigene Stufe zu geben (und damit
   eine dritte Flaechenfarbe einzufuehren) machen sie schlicht nichts.

   Das trifft auch die Listen-Darstellung der Datatable (`presentation="list"`,
   12 Aufrufer), die ueber `v-list-item` statt `v-table` laeuft und von jeder
   Tabellenregel unberuehrt bleibt.

   `.v-main` ist hier wichtig: der Navigations-Drawer und die Menues im
   .v-overlay-container SOLLEN ihre eigene Flaeche behalten. */
.v-main .v-list {
	background: transparent;
}

/* Die Suchleiste sitzt seit dem flachen Layout auf dem Canvas und war als
   einziges Element noch reinweiss — bei Boehm ein weisser Kasten ueber einer
   gelben Flaeche ueber einer getoenten Tabelle. `solo` fuellt ueber
   `--v-theme-surface`; hier laeuft sie auf derselben Flaeche wie die Tabelle,
   zu der sie gehoert. */
.v-main .v-field--variant-solo,
.v-main .v-field--variant-solo-filled {
	background-color: var(--tenant-surface-1);
}

/* ---------------------------------------------------------------------------
   Knoepfe ohne eigene Farbe.

   `variant="outlined"` und die Vuetify-Vorgabe `flat` bringen keine eigene
   Flaeche mit, sie zeigen schlicht das, worauf sie liegen. Auf Boehms Gelb
   heisst das: der Knopf IST der Hintergrund, erkennbar nur am Rahmen. Ein
   Bedienelement, das man nur an seiner Umrandung erkennt, ist keins.

   Sie bekommen deshalb die Struktur-Stufe — dieselbe wie Tabellenkopf und
   Dialogkopf. Gegen den vollen Mandanten-Ton traegt nicht die Helligkeit den
   Unterschied (L 0.91 gegen 0.87 waere zu wenig), sondern die gekappte
   Saettigung: die Flaeche ist deutlich blasser als der Canvas.

   Ohne `!important`, also behalten gefaerbte Knoepfe (`bg-primary`,
   `bg-warning`, der rote Hausverwalter-Knopf) ihre Farbe von selbst. `text`-
   und `plain`-Varianten bleiben bewusst aussen vor: die sollen flaechenlos
   sein. */
.v-main .v-btn--variant-flat,
.v-main .v-btn--variant-elevated,
.v-main .v-btn--variant-outlined {
	background-color: var(--tenant-surface-2);
}

/* Segmentierte Umschalter (ALLE/MEINE, AKTIVE/ALLE) als Schiene mit
   ausgeschnittenem aktiven Feld — das laeuft in jedem Theme, weil beide
   Zustaende aus derselben Leiter kommen und nicht aus einer Mandantenfarbe,
   die auch der Hintergrund sein koennte.

   Die Schiene steht auf der Struktur-Stufe, das aktive Feld eine Stufe heller
   auf der Arbeitsflaeche. Die Mandanten-Primaerfarbe bleibt als SCHRIFTfarbe
   des aktiven Feldes erhalten (`color="primary"` am Umschalter) — das lebende
   Element behaelt damit sein Signal, ohne dass die Lesbarkeit daran haengt. */
/* `.v-btn-group.v-btn-toggle` statt nur `.v-btn-toggle`, und unten entsprechend
   eine Klasse mehr: Vuetifys CSS liegt in diesem Projekt ZWEIMAL im Dokument
   (zwei `*, ::before, ::after`-Resets, zwei `.v-btn-group`-Bloecke — die
   gebuendelte `vuetify.min.css` plus die vom Vite-Plugin je Komponente
   nachgeladenen Styles). Die zweite Kopie landet NACH den Styles aus dieser
   Datei. Bei gleicher Spezifitaet entscheidet die Reihenfolge, und die
   verliert man hier still — die Regel steht im Stylesheet, wirkt aber nicht.
   Eine Klasse mehr macht die Regel unabhaengig von der Ladereihenfolge. */
.v-btn-group.v-btn-toggle {
	background-color: var(--tenant-surface-1);
}

.v-btn-group.v-btn-toggle .v-btn {
	background-color: transparent;
}

/* Das aktive Feld ist das DUNKLERE, nicht das hellere. Umgekehrt herum gebaut
   sah es besser aus — bis man nachmisst: Vuetify legt auf den aktiven Knopf
   zusaetzlich ein Overlay aus der Mandanten-Primaerfarbe (12 %), das sich
   nicht wegregeln liess (die Regel steht im Stylesheet, das Element trifft
   sie, sie wirkt trotzdem nicht). Bei Boehm ist Primaer fast schwarz — das
   helle aktive Feld wurde dadurch auf genau die Helligkeit der Schiene
   heruntergezogen und beide Zustaende sahen gleich aus.

   Andersherum arbeitet das Overlay mit statt dagegen: Stufe 2 plus 12 %
   Primaerfarbe ergibt ein klar dunkleres Feld auf heller Schiene. Die
   Schriftfarbe bleibt die Primaerfarbe, das lebende Element behaelt also sein
   Signal. */
.v-btn-group.v-btn-toggle .v-btn--active {
	background-color: var(--tenant-surface-2);
}

/* Ladeplatzhalter. Vuetify faerbt die Balken mit einem festen Grau; auf einer
   getoenten Flaeche liest sich das als Stoerung statt als Platzhalter. Sie
   laufen auf derselben Leiter wie die Zeilen, die sie gleich ersetzen, damit
   der Wechsel von Laden zu Daten keinen Farbsprung macht.

   Global und nicht je Komponente: es gibt keinen eigenen Skeleton-Wrapper,
   `v-skeleton-loader` steht an vier Stellen direkt im Markup. */
.v-main .v-skeleton-loader {
	background-color: transparent;

	.v-skeleton-loader__bone {
		background-color: var(--tenant-surface-2);
	}
}

/* Die Breadcrumb-Spur traegt den Weg, nicht den Inhalt. Die zurueckliegenden
   Stationen stehen deshalb gedaempft, die aktuelle Seite als einzige in voller
   Schriftfarbe — sie ist das Ziel. Bisher stand die gesamte Zeile gleich laut,
   inklusive der Station, auf der man gerade steht.

   Gedaempft ueber die SCHRIFTFARBE, nicht ueber die Mandanten-Akzentfarbe.
   Erster Versuch war Letzteres, und die Zeile verschwand bei Boehm komplett:
   deren Sekundaerfarbe ist exakt dasselbe Gelb wie der Hintergrund. Text auf
   einer Flaeche, die der Mandant frei waehlt, darf seine Farbe nicht aus einer
   zweiten Farbe beziehen, die derselbe Mandant genauso frei waehlt — sonst
   entscheidet der Zufall ueber die Lesbarkeit. Die Identitaet traegt hier die
   Flaeche, der Text traegt den Kontrast (BITV/WCAG AA). */
.v-main .v-breadcrumbs-item--link {
	opacity: 0.6;
}

/* `--active` am <li>, nicht `--disabled`. Erster Versuch stand auf
   `.v-breadcrumbs-item--disabled` — die Klasse vergibt Vuetify 3 hier gar
   nicht, der Selektor traf NULL Elemente. Die Daempfung lag damit auf beiden
   Stationen, auch auf der, wo man gerade steht: die Regel tat das Gegenteil
   dessen, was zwei Absaetze darueber stand. Nachgemessen im Browser
   (`--active` am li, `--link` am a darin). */
.v-main .v-breadcrumbs-item--active .v-breadcrumbs-item--link {
	opacity: 1;
}
</style>
